export const SOLUTION_SLUGS = [
  "social-sellers",
  "fashion-lifestyle",
  "food-beverage",
  "services-bookings",
] as const

export type SolutionSlug = (typeof SOLUTION_SLUGS)[number]

export type SolutionContent = {
  slug: SolutionSlug
  title: string
  tagline: string
  intro: string
  painPoints: string[]
  howSqaleshopHelps: string[]
  outcomes: string[]
  image: string
}

export const solutions: Record<SolutionSlug, SolutionContent> = {
  "social-sellers": {
    slug: "social-sellers",
    title: "WhatsApp & Instagram sellers",
    tagline: "From scattered DMs to one clean store link",
    intro:
      "Sqaleshop is built for small businesses who sell through WhatsApp, Instagram, and other social channels. You get one professional storefront where customers browse, place orders, and pay without endless back-and-forth.",
    painPoints: [
      "Orders and questions get lost in long chat threads.",
      "You repeat prices, sizes, and availability dozens of times a day.",
      "There is no single place to see what is in stock or who bought what.",
      "Generic store builders feel too technical, expensive, or bloated for how you actually sell.",
    ],
    howSqaleshopHelps: [
      "Share one link in your bio or status.",
      "Central dashboard for products, orders, and customers replaces mental load and spreadsheets.",
      "Structured checkout-style flow cuts down on “how much is this?” messages.",
      "Start free, then scale to Premium when you are ready for more capacity and features.",
    ],
    outcomes: [
      "Fewer missed orders and fewer repeated answers.",
      "Clearer view of growth beyond what you can remember from DMs.",
      "Professional presence that still feels like you.",
    ],
    image:
      "https://images.unsplash.com/photo-1600859343194-a5fae9ef9f66?q=80&w=1074&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D?w=900&h=600&fit=crop&q=80",
  },
  "fashion-lifestyle": {
    slug: "fashion-lifestyle",
    title: "Fashion & lifestyle brands",
    tagline: "Drops, sizes, and restocks without the chaos",
    intro:
      "Whether you sell from your bedroom or a small studio, Sqaleshop gives you structure for variants, collections, and repeat customers, so your Instagram hype turns into fulfilled orders, not confused DMs.",
    painPoints: [
      "Size and color options are hard to explain cleanly in chat.",
      "Restocks and sold-out SKUs are awkward to broadcast to everyone individually.",
      "You need a link that looks as good as your feed.",
    ],
    howSqaleshopHelps: [
      "Organize products and options so shoppers self-serve before they ping you.",
      "One storefront link for campaigns, stories, and customer service.",
      "Order history tied to customers helps you recognize VIPs and handle exchanges.",
    ],
    outcomes: [
      "Cleaner launches and less time clarifying the same product details.",
      "A brand home that matches how you sell today: social-first, not enterprise-first.",
    ],
    image:
      "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=900&h=600&fit=crop&q=80",
  },
  "food-beverage": {
    slug: "food-beverage",
    title: "Food & beverage",
    tagline: "Menus, bundles, and busy-hour clarity",
    intro:
      "From home kitchens to local delivery, Sqaleshop helps you present menus and bundles clearly, take orders in one flow, and keep fulfillment sane when notifications are blowing up.",
    painPoints: [
      "Peak hours mean duplicate orders and wrong items in chat.",
      "Specials and combos are painful to explain line by line.",
      "You need today’s offer to be obvious without retyping it fifty times.",
    ],
    howSqaleshopHelps: [
      "Product pages for dishes, packs, and add-ons. Customers choose before they message.",
      "Orders land in one queue you can work through with your team.",
      "Optional bookings or slots when you offer pickup windows or tastings.",
    ],
    outcomes: [
      "Less friction during rush periods.",
      "Fewer mistakes from misread messages.",
    ],
    image:
      "https://images.unsplash.com/photo-1539755530862-00f623c00f52?q=80&w=1470&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D?w=900&h=600&fit=crop&q=80",
  },
  "services-bookings": {
    slug: "services-bookings",
    title: "Services & appointments",
    tagline: "Replace “what times work?” with structured availability",
    intro:
      "Consultants, stylists, tutors, and studios use Sqaleshop to package what they sell, let clients book or order in one place, and keep customer context next to every appointment.",
    painPoints: [
      "Calendar ping-pong in DMs eats your day.",
      "Deposits and packages are hard to track per client.",
      "No single record of who booked what and when.",
    ],
    howSqaleshopHelps: [
      "Booking slots and service products live beside physical items if you sell both.",
      "Customer profiles hold history so follow-ups feel personal, not improvised.",
      "Dashboard view so you are not relying on screenshots and voice notes alone.",
    ],
    outcomes: [
      "More time delivering the service, less time negotiating time slots.",
      "Clearer pipeline from inquiry to paid session.",
    ],
    image:
      "https://images.unsplash.com/photo-1521791136064-7986c2920216?w=900&h=600&fit=crop&q=80",
  },
}

export function isSolutionSlug(s: string): s is SolutionSlug {
  return (SOLUTION_SLUGS as readonly string[]).includes(s)
}
